/**
 * Screens 13 «Услуги» and 14 «Правка услуги» (salon-mockup.html services /
 * serviceedit). A list on a phone, a table on desktop; «+» and every row open
 * sheet 14 over the list. «Свой цвет» has no picker drawn yet, so it is shown
 * disabled; «Как статус» saves no colour.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';

import { ActionMenu } from '../components/ActionMenu';
import { AppBar, Sheet, Shell } from '../components/Shell';
import { Icon } from '../components/icons';
import { ApiError, api } from '../core/api';
import { errorText, formatAmount } from '../i18n';
import { serviceName } from '../lib/serviceName';
import type { Locale, Service } from '../types';

export function ServicesScreen() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language as Locale;
  const navigate = useNavigate();
  const { id } = useParams();
  const services = useQuery({ queryKey: ['services'], queryFn: api.services });
  const open = (to: string) => navigate(`/settings/services/${to}`);
  const editing = id === 'new' ? null : services.data?.find((s) => s.id === id);
  const nameCols: Locale[] = locale === 'ky' ? ['ky', 'ru'] : ['ru', 'ky'];

  return (
    <Shell active="services">
      <AppBar
        title={t('services.title')}
        right={
          <button type="button" className="iconbtn" aria-label={t('services.add')} onClick={() => open('new')}>
            <Icon.plus />
          </button>
        }
      />
      <div className="content">
        <div className="pane">
          <div className="mob-only">
            <ul className="list">
              {services.data?.map((s) => (
                <li key={s.id} data-id={s.id} onClick={() => open(s.id)}>
                  <span className="grow">
                    <span className="nm">{serviceName(s.name_ru, s.name_ky, locale)}</span>
                    <span className="sm" style={{ display: 'block' }}>
                      {s.duration_minutes} {t('dur.min')}
                    </span>
                  </span>
                  <b className="tnum" style={{ color: 'var(--ink)', fontSize: 14 }}>
                    {formatAmount(s.price, locale)}
                  </b>
                </li>
              ))}
            </ul>
          </div>
          <div className="desk-only">
            <table className="tbl">
              <thead>
                <tr>
                  {/* Her own language's column first (D-67). */}
                  {nameCols.map((c) => (
                    <th key={c}>{t(c === 'ky' ? 'services.nameKyCol' : 'services.nameRuCol')}</th>
                  ))}
                  <th className="r">{t('services.duration')}</th>
                  <th className="r">{t('services.price')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {services.data?.map((s) => (
                  <tr key={s.id} data-id={s.id} onClick={() => open(s.id)}>
                    {nameCols.map((c, i) => {
                      const name = (c === 'ky' ? s.name_ky : s.name_ru) || '—';
                      return <td key={c}>{i === 0 ? <b>{name}</b> : name}</td>;
                    })}
                    <td className="r">
                      {s.duration_minutes} {t('dur.min')}
                    </td>
                    <td className="r">
                      <b>{formatAmount(s.price, locale)}</b>
                    </td>
                    <td className="r">
                      <span className="xs">{t('services.edit')}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {id && (id === 'new' || editing) && (
        <ServiceSheet key={id} service={editing ?? null} onClose={() => navigate('/settings/services')} />
      )}
    </Shell>
  );
}

function ServiceSheet({ service, onClose }: { service: Service | null; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language as Locale;
  const qc = useQueryClient();
  const [nameRu, setNameRu] = useState(service?.name_ru ?? '');
  const [nameKy, setNameKy] = useState(service?.name_ky ?? '');
  const [minutes, setMinutes] = useState(service ? String(service.duration_minutes) : '');
  const [price, setPrice] = useState(service ? String(Number(service.price)) : '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => setError(null), [nameRu, nameKy, minutes, price]);
  const ru = ['services.nameRu', nameRu, setNameRu] as const;
  const ky = ['services.nameKy', nameKy, setNameKy] as const;
  const nameFields = locale === 'ky' ? [ky, ru] : [ru, ky];
  const more = useRef<HTMLButtonElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = useCallback(() => setMenuOpen(false), []);
  const title = service ? serviceName(service.name_ru, service.name_ky, locale) : t('services.new');

  // A new, changed or retired service changes the free gaps (D-37) and «Добавьте услуги».
  const refresh = () => {
    for (const key of ['services', 'today', 'day', 'week', 'month']) void qc.invalidateQueries({ queryKey: [key] });
  };

  /** Screen 14b (D-68): the «…» menu was the confirmation. */
  const remove = async () => {
    if (!service) return;
    setMenuOpen(false);
    setBusy(true);
    try {
      await api.deleteService(service.id);
      refresh();
      onClose();
    } catch (err) {
      setError(errorText(err instanceof ApiError ? err.code : 'fallback'));
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if ((!nameRu.trim() && !nameKy.trim()) || !(Number(minutes) > 0) || price === '' || Number(price) < 0) {
      return setError(t('services.fill'));
    }
    const body = { name_ru: nameRu.trim(), name_ky: nameKy.trim(), duration_minutes: Number(minutes), price };
    setBusy(true);
    try {
      if (service) await api.updateService(service.id, body);
      else await api.createService(body);
      refresh();
      onClose();
    } catch (err) {
      setError(errorText(err instanceof ApiError ? err.code : 'fallback'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet onClose={onClose} className="narrow">
      <form onSubmit={submit} noValidate>
        <div className="sh-head">
          <h3>{title}</h3>
          {service && (
            <button
              ref={more}
              type="button"
              className="iconbtn more"
              aria-label={t('services.more')}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              disabled={busy}
              onClick={() => setMenuOpen(true)}
            >
              ···
            </button>
          )}
        </div>
        {error && <div className="warnbox form-error" role="alert">{error}</div>}
        {/* Her own language's name first; one of the two is enough (D-67). */}
        {nameFields.map(([label, value, set]) => (
          <div className="fld" key={label}>
            <label>
              {t(label)}
              <input className="inp" value={value} onChange={(e) => set(e.target.value)} maxLength={120} />
            </label>
          </div>
        ))}
        <div className="row" style={{ gap: 11, alignItems: 'flex-start' }}>
          <div className="fld grow">
            <label>{t('services.duration')}</label>
            <div className="inp">
              <input aria-label={t('services.duration')} className="bare" type="number" min={5} step={5} value={minutes} onChange={(e) => setMinutes(e.target.value)} style={{ width: `${Math.max(minutes.length, 2) + 1}ch` }} />
              <span>{t('dur.min')}</span>
            </div>
          </div>
          <div className="fld grow">
            <label>{t('services.price')}</label>
            <div className="inp">
              <input aria-label={t('services.price')} className="bare" type="number" min={0} value={price} onChange={(e) => setPrice(e.target.value)} style={{ width: `${Math.max(price.length, 2) + 1}ch` }} />
              <span>{t('money.som')}</span>
            </div>
          </div>
        </div>
        <div className="fld">
          <label>{t('services.colour')}</label>
          <div className="chips">
            <span className="chip on">{t('services.colourStatus')}</span>
            <span className="chip" aria-disabled="true" title={t('app.soon')} style={{ opacity: 0.5, cursor: 'default' }}>
              {t('services.colourOwn')}
            </span>
          </div>
        </div>
        <div className="okbox">{t('services.note')}</div>
        <div className="btnrow">
          <button type="button" className="btn" onClick={onClose}>
            {t('services.cancel')}
          </button>
          <button type="submit" className="btn pri" disabled={busy}>
            {t('services.save')}
          </button>
        </div>
      </form>
      {menuOpen && service && more.current && (
        <ActionMenu
          anchor={more.current}
          note={t('services.deleteWarn')}
          action={t('services.deleteNamed', { name: title })}
          cancel={t('services.cancel')}
          onAction={() => void remove()}
          onClose={closeMenu}
        />
      )}
    </Sheet>
  );
}
